import React from "react";
import { Slot } from "@radix-ui/react-slot";
import {
  Controller,
  type FieldPath,
  FormProvider,
  type FieldValues,
  type UseFormProps,
  type UseFormReturn,
  type UseWatchProps,
  useController,
  useForm as useReactHookForm,
  useWatch as useReactHookFormWatch,
  type UseFormRegisterReturn,
  useFormContext as useReactHookFormContext,
  useFormState as useReactHookFormState,
  createFormControl,
  type UseFormStateProps,
  type UseFormStateReturn,
} from "react-hook-form";
import { type ZodTypeAny } from "zod";
import { get } from "lodash";
import { useDismissGuard } from "../../common/DismissGuard/DismissGuard";
import ConfirmDialog from "../../common/ConfirmDialog/ConfirmDialog";
import { isFieldRequiredBySchema } from "./FormValidation";

export type FormReturn<TFieldValues extends FieldValues = FieldValues> = Omit<
  UseFormReturn<TFieldValues>,
  "formState" | "watch"
> & {
  /**
   *  @deprecated Use useFormState() for reactive form state subscriptions.
   */
  formState: never;
  /**
   *  @deprecated Use <FieldWatch> for reactive form state subscriptions.
   */
  watch: never;
};

export const useFormContext = <TFieldValues extends FieldValues = FieldValues>() => {
  return useReactHookFormContext<TFieldValues>() as FormReturn<TFieldValues>;
};

export { Controller, FormProvider, useController };

export const useFormState = <TFieldValues extends FieldValues = FieldValues, TTransformedValues = TFieldValues>(props?: UseFormStateProps<TFieldValues, TTransformedValues>) : UseFormStateReturn<TFieldValues> | undefined => {
  const form = useFormContext();
  const formState = useReactHookFormState({
    ...props,
    // @ts-ignore
    control: props?.control ?? form?.control ?? STUB_CONTROL.control
  });

  // @ts-ignore
  return form ? formState : undefined;
}

export const useWatch = <TFieldValues extends FieldValues = FieldValues>(
  props?: UseWatchProps<TFieldValues>,
) => {
  const form = useFormContext<TFieldValues>();
  const watchProps = {
    ...props,
    control: props?.control ?? form?.control ?? STUB_CONTROL.control,
  } as UseWatchProps<TFieldValues>;
  return useReactHookFormWatch<TFieldValues>(watchProps as any);
};

export interface FieldWatchProps<
  TFieldValues extends FieldValues = FieldValues,
  TNames extends readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
> {
  names: TNames;
  children: (values: Record<TNames[number], any>) => React.ReactNode;
}

export const FieldWatch = <
  TFieldValues extends FieldValues = FieldValues,
  const TNames extends readonly FieldPath<TFieldValues>[] = readonly FieldPath<TFieldValues>[],
>({
  children,
  names,
}: FieldWatchProps<TFieldValues, TNames>) => {
  const form = useFormContext();
  const watchedValues = useWatch<TFieldValues>({ control: form?.control, name: names as any } as UseWatchProps<TFieldValues>);
  const valueEntries = names.map((name, index) => [name, (watchedValues as unknown[])[index]]);
  const values = Object.fromEntries(valueEntries) as Record<TNames[number], any>;
  return <>{children(values)}</>;
};

export interface FormError {
  message: string;
}

export interface UseFieldResult<TValue = unknown> {
  value: TValue | undefined;
  ref: UseFormRegisterReturn["ref"] | undefined;
  error: FormError | undefined;
  required: boolean;
  onChange: (eventOrValue: unknown) => void;
  onValueChange: (value: TValue) => void;
  onBlur: () => void;
  setValue: (nextValue: TValue, options?: { shouldDirty?: boolean; shouldTouch?: boolean; shouldValidate?: boolean }) => void;
}

export interface UseFieldOptions<TValue = unknown> {
  name?: string;
  value?: TValue;
  onChange?: (...args: any[]) => void;
  onBlur?: (...args: any[]) => void;
  onValueChange?: (value: TValue) => void;
  required?: boolean;
}

const STUB_CONTROL = createFormControl();
const FormSchemaContext = React.createContext<ZodTypeAny | undefined>(undefined);

export const useField = <TValue = unknown>({
  name,
  value: controlledValue,
  onChange: userOnChange,
  onBlur: userOnBlur,
  onValueChange: userOnValueChange,
  required,
}: UseFieldOptions<TValue>): UseFieldResult<TValue> => {
  const form = useFormContext();
  const control = form?.control ?? STUB_CONTROL.control;
  const schema = React.useContext(FormSchemaContext);

  const resolvedRequired = React.useMemo(
    () => Boolean(required || (name && isFieldRequiredBySchema(schema, name))),
    [schema, required, name],
  );
  const field = name ? form?.register(name, {
    required: resolvedRequired ? "Required" : false,
  }) : undefined;


  const watchedValue = useWatch({ control: control, name }) as TValue | undefined;
  const value = controlledValue !== undefined ? controlledValue : watchedValue;


  const formState = useFormState({
    control,
    name: name,
  });
  const error = name && formState ? get(formState?.errors, name) as FormError : undefined;

  const setValue = React.useCallback((nextValue: TValue, options?: { shouldDirty?: boolean; shouldTouch?: boolean; shouldValidate?: boolean }) => {
    if (!form) return;
    form.setValue(name as any, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
      ...options,
    });
  }, [form, name]);

  const commitValue = React.useCallback((nextValue: TValue) => {
    userOnValueChange?.(nextValue);
    const syntheticEvent = {
      target: {
        name,
        value: nextValue,
      },
    } as any;
    userOnChange?.(syntheticEvent);
    form?.setValue(name as any, nextValue, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [form, name, userOnChange, userOnValueChange]);

  const onValueChange = React.useCallback((nextValue: TValue) => {
    commitValue(nextValue);
  }, [commitValue]);

  const onChange = React.useCallback((event: any) => {
    const nextValue = event?.target?.type === "checkbox"
      ? event?.target?.checked
      : event?.target?.value;
    commitValue(nextValue as TValue);
  }, [commitValue]);

  const onBlur = React.useCallback((event?: any) => {
    userOnBlur?.(event);
    field?.onBlur?.(event);
  }, [form, name, userOnBlur]);

  return { value, ref: field?.ref, error, required: resolvedRequired, onChange, onValueChange, onBlur, setValue };
};

export interface FormProps<
  TFieldValues extends FieldValues = FieldValues,
  TSchema extends ZodTypeAny | undefined = undefined,
> extends Omit<React.ComponentPropsWithoutRef<"form">, "onSubmit" | "children"> {
  asChild?: boolean;
  handleRef?: React.Ref<FormReturn<TFieldValues>>;
  children: React.ReactNode | ((methods: FormReturn<TFieldValues>) => React.ReactNode);
  defaultValues?: UseFormProps<TFieldValues>["defaultValues"];
  options?: Omit<UseFormProps<TFieldValues>, "resolver" | "defaultValues" | "mode">;
  schema?: TSchema;
  mode?: UseFormProps<TFieldValues>["mode"];
  onSubmit?: (values: TFieldValues, methods: UseFormReturn<TFieldValues>) => void | Promise<void>;
  /**
   * When `true`, activates a dismiss guard that prevents accidental data loss.
   *
   * While the form is dirty, closing the dialog (overlay click, Escape, close button)
   * shows a `<ConfirmDialog>` asking the user to confirm discarding changes.
   * Browser tab close and in-app route navigation are guarded too.
   */
  dismissGuard?: boolean;
}

/**
 * Internal bridge: subscribes to form dirty state and activates the dismiss guard.
 * Renders its own ConfirmDialog — no external wiring needed.
 * Must be rendered inside a FormProvider.
 */
const DismissGuardBridge = () => {
  const formState = useFormState();
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [pendingProceed, setPendingProceed] = React.useState<(() => void) | null>(null);

  const handleAttempt = React.useCallback((proceed: () => void) => {
    setPendingProceed(() => proceed);
    setConfirmOpen(true);
  }, []);

  useDismissGuard({
    when: Boolean(formState?.isDirty),
    onAttempt: handleAttempt,
  });

  return (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title="Unsaved changes"
      description="You have unsaved changes. If you close now your changes will be lost."
      confirmLabel="Discard changes"
      cancelLabel="Keep editing"
      confirmIntent="destructive"
      onConfirm={() => {
        const proceed = pendingProceed;
        setConfirmOpen(false);
        setPendingProceed(null);
        proceed?.();
      }}
    />
  );
};

const Form = <
  TFieldValues extends FieldValues = FieldValues,
  TSchema extends ZodTypeAny | undefined = undefined,
>({
  asChild,
  handleRef,
  children,
  defaultValues,
  mode = "onChange",
  schema,
  options,
  onSubmit,
  dismissGuard,
  ...otherProps
}: FormProps<TFieldValues, TSchema>) => {
  const resolver = React.useMemo(() => {
    if (!schema) return undefined;
    return async (values: unknown) => {
      const result = await schema.safeParseAsync(values);
      if (result.success) {
        return {
          values: result.data,
          errors: {},
        };
      }

      const errors = result.error.issues.reduce<Record<string, { type: string; message: string }>>((acc, issue) => {
        const path = issue.path.join(".");
        if (path && !acc[path]) {
          acc[path] = {
            type: issue.code,
            message: issue.message,
          };
        }
        return acc;
      }, {});

      return {
        values: {},
        errors,
      };
    };
  }, [schema]);

  const methods = useReactHookForm<TFieldValues>({
    ...options,
    defaultValues,
    mode,
    resolver: resolver as any,
  });
  const formReturn = methods as unknown as FormReturn<TFieldValues>;
  React.useImperativeHandle(handleRef, () => formReturn, [formReturn]);

  const Component = asChild ? Slot : "form";

  const handleSubmit = onSubmit
    ? methods.handleSubmit(async (formValues: TFieldValues) => {
        if (typeof window !== "undefined" && process.env.NODE_ENV === "test") {
          window.dispatchEvent(
            new window.CustomEvent("deci:form:submit", {
              detail: { values: formValues },
            }),
          );
        }
        await onSubmit(formValues, methods);
      })
    : undefined;

  return (
    <FormSchemaContext.Provider value={schema}>
      <FormProvider {...methods}>
        {dismissGuard && <DismissGuardBridge />}
        <Component {...otherProps} onSubmit={handleSubmit}>
          {typeof children === "function" ? children(formReturn) : children}
        </Component>
      </FormProvider>
    </FormSchemaContext.Provider>
  );
};

export default Form;
